import React, { useState, useEffect, useCallback } from "react";
import scheduledImportService from "../../services/scheduledImportService";
import LoadingSpinner from "../common/LoadingSpinner";
import ScheduledImportForm from "./ScheduledImportForm";
import {
  FaEdit,
  FaPlay,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaCloud,
  FaHdd,
  FaExternalLinkAlt,
  FaFileContract,
  FaMoneyBill,
  FaSpinner,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaSync,
  FaExclamationTriangle,
} from "react-icons/fa";

const ScheduledImportsList = () => {
  const [scheduledImports, setScheduledImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentImport, setCurrentImport] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [runningImports, setRunningImports] = useState(new Set());
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadScheduledImports();
  }, []);

  // 🔧 SUPPRIMÉ: calculateVersementTime (plus de gestion automatique des versements)

  // 🔧 HELPER: Vérifier si on est dans la plage horaire
  const isCurrentlyInTimeWindow = (dailyStartTime, dailyEndTime) => {
    if (!dailyStartTime || !dailyEndTime) return true;

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const [startH, startM] = dailyStartTime.split(":").map(Number);
    const [endH, endM] = dailyEndTime.split(":").map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    if (endMinutes < startMinutes) {
      // Plage qui traverse minuit (ex: 22:00 - 06:00)
      return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
    } else {
      // Plage normale (ex: 08:00 - 18:00)
      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    }
  };

  // 🔧 HELPER: Calculer le temps restant jusqu'à la prochaine exécution
  const getTimeUntilNext = (nextRun) => {
    if (!nextRun) return "❌ Non programmé";

    const now = new Date();
    const nextDate = new Date(nextRun);
    const diffMs = nextDate - now;

    if (diffMs <= 0) return "⚡ Imminent";

    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `⏰ Dans ${diffDays}j ${diffHours % 24}h`;
    if (diffHours > 0) return `⏰ Dans ${diffHours}h ${diffMins % 60}min`;
    return `⏰ Dans ${diffMins}min`;
  };

  const loadScheduledImports = async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const response = await scheduledImportService.getScheduledImports();
      setScheduledImports(response.data);
      setError(null);
    } catch (err) {
      setError("Erreur lors du chargement des importations planifiées");
      console.error(err);
    } finally {
      if (showLoader) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  const silentRefresh = useCallback(async () => {
    try {
      const response = await scheduledImportService.getScheduledImports();
      setScheduledImports(response.data);
    } catch (err) {
      console.error("Erreur lors du rafraîchissement silencieux:", err);
    }
  }, []);

  // 🔧 RAFRAÎCHISSEMENT AUTOMATIQUE toutes les 30 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      silentRefresh();
    }, 30000);
    return () => clearInterval(interval);
  }, [silentRefresh]);

  const updateScheduledImportLocally = useCallback((importId, updates) => {
    setScheduledImports((prevImports) =>
      prevImports.map((imp) =>
        imp._id === importId ? { ...imp, ...updates } : imp
      )
    );
  }, []);

  const handleCreate = () => {
    setCurrentImport(null);
    setShowModal(true);
  };

  const handleEdit = (scheduledImport) => {
    setCurrentImport(scheduledImport);
    setShowModal(true);
  };

  const handleFormSubmit = async (values) => {
    try {
      if (currentImport) {
        await scheduledImportService.updateScheduledImport(
          currentImport._id,
          values
        );
      } else {
        await scheduledImportService.createScheduledImport(values);
      }
      setShowModal(false);
      await loadScheduledImports(false);
    } catch (err) {
      console.error("Erreur lors de l'enregistrement:", err);
      setError(err.message || "Une erreur est survenue");
    }
  };

  const handleToggleStatus = async (scheduledImport) => {
    try {
      updateScheduledImportLocally(scheduledImport._id, {
        isActive: !scheduledImport.isActive,
      });

      await scheduledImportService.updateScheduledImport(scheduledImport._id, {
        isActive: !scheduledImport.isActive,
      });

      setTimeout(silentRefresh, 1000);
    } catch (err) {
      console.error("Erreur lors de la modification du statut:", err);
      setError(err.message || "Une erreur est survenue");
      updateScheduledImportLocally(scheduledImport._id, {
        isActive: scheduledImport.isActive,
      });
    }
  };

  // 🔧 GESTION AMÉLIORÉE de l'exécution manuelle avec surveillance en temps réel
  const handleRun = async (scheduledImport) => {
    try {
      console.log(
        `🚀 Lancement de l'importation: ${scheduledImport.description}`
      );

      setRunningImports((prev) => new Set([...prev, scheduledImport._id]));

      // 🔧 MISE À JOUR IMMÉDIATE du statut local
      updateScheduledImportLocally(scheduledImport._id, {
        lastStatus: "running",
        lastRun: new Date().toISOString(),
      });

      await scheduledImportService.runScheduledImport(scheduledImport._id);

      console.log(`✅ Importation lancée avec succès`);

      // 🔧 SURVEILLANCE RENFORCÉE avec vérifications plus fréquentes
      let attempts = 0;
      const maxAttempts = 40; // Augmenté pour les gros fichiers

      const checkStatus = async () => {
        attempts++;
        console.log(
          `🔍 Vérification ${attempts}/${maxAttempts} pour ${scheduledImport.description}`
        );

        try {
          const response = await scheduledImportService.getScheduledImports();
          const updatedImport = response.data.find(
            (imp) => imp._id === scheduledImport._id
          );

          if (updatedImport) {
            updateScheduledImportLocally(scheduledImport._id, {
              lastStatus: updatedImport.lastStatus,
              lastRun: updatedImport.lastRun,
              nextRun: updatedImport.nextRun,
              lastError: updatedImport.lastError,
            });

            if (updatedImport.lastStatus !== "running") {
              console.log(
                `✅ Importation terminée avec statut: ${updatedImport.lastStatus}`
              );
              console.log(
                `📅 Prochaine exécution: ${
                  updatedImport.nextRun
                    ? new Date(updatedImport.nextRun).toLocaleString()
                    : "Non définie"
                }`
              );

              setRunningImports((prev) => {
                const newSet = new Set(prev);
                newSet.delete(scheduledImport._id);
                return newSet;
              });

              return;
            } else if (attempts < maxAttempts) {
              // 🔧 VÉRIFICATION plus fréquente: toutes les 1.5 secondes
              setTimeout(checkStatus, 1500);
            } else {
              console.log(
                `⏰ Timeout de surveillance pour ${scheduledImport.description}`
              );
              setRunningImports((prev) => {
                const newSet = new Set(prev);
                newSet.delete(scheduledImport._id);
                return newSet;
              });
            }
          }
        } catch (err) {
          console.error("Erreur lors de la vérification:", err);
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 1500);
          } else {
            setRunningImports((prev) => {
              const newSet = new Set(prev);
              newSet.delete(scheduledImport._id);
              return newSet;
            });
          }
        }
      };

      // 🔧 DÉMARRAGE immédiat de la surveillance
      setTimeout(checkStatus, 2000);
    } catch (err) {
      console.error("Erreur lors du lancement de l'importation:", err);
      setError(err.message || "Une erreur est survenue");

      setRunningImports((prev) => {
        const newSet = new Set(prev);
        newSet.delete(scheduledImport._id);
        return newSet;
      });

      updateScheduledImportLocally(scheduledImport._id, {
        lastStatus: scheduledImport.lastStatus,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await scheduledImportService.deleteScheduledImport(confirmDelete._id);
      setConfirmDelete(null);
      await loadScheduledImports(false);
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
      setError(err.message || "Une erreur est survenue");
    }
  };

  const getTypeIcon = (importType) => {
    return importType === "google_drive" ? (
      <FaCloud className="text-green-600" title="Google Drive" />
    ) : (
      <FaHdd className="text-blue-600" title="Fichier Local" />
    );
  };

  const getDataTypeIcon = (dataType) => {
    return dataType === "versements" ? (
      <FaMoneyBill className="text-yellow-600" title="Versements" />
    ) : (
      <FaFileContract className="text-purple-600" title="Contrats" />
    );
  };

  const getTypeLabel = (importType) => {
    return importType === "google_drive" ? "Google Drive" : "Fichier Local";
  };

  const getDataTypeLabel = (dataType) => {
    return dataType === "versements" ? "Versements" : "Contrats";
  };

  const getSourceInfo = (scheduledImport) => {
    if (scheduledImport.dataType === "versements") {
      if (scheduledImport.importType === "google_drive") {
        return (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 truncate max-w-xs">
              {scheduledImport.googleDriveVersementFileName ||
                `ID: ${scheduledImport.googleDriveVersementFileId?.slice(
                  0,
                  15
                )}...`}
            </span>
            <a
              href={`https://drive.google.com/file/d/${scheduledImport.googleDriveVersementFileId}/view`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-800"
              title="Ouvrir dans Google Drive"
            >
              <FaExternalLinkAlt className="w-3 h-3" />
            </a>
          </div>
        );
      } else {
        return (
          <span className="text-sm text-gray-600 truncate max-w-xs">
            {scheduledImport.versementFilePath}
          </span>
        );
      }
    } else {
      if (scheduledImport.importType === "google_drive") {
        return (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 truncate max-w-xs">
              {scheduledImport.googleDriveFileName ||
                `ID: ${scheduledImport.googleDriveFileId?.slice(0, 15)}...`}
            </span>
            <a
              href={`https://drive.google.com/file/d/${scheduledImport.googleDriveFileId}/view`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-800"
              title="Ouvrir dans Google Drive"
            >
              <FaExternalLinkAlt className="w-3 h-3" />
            </a>
          </div>
        );
      } else {
        return (
          <span className="text-sm text-gray-600 truncate max-w-xs">
            {scheduledImport.filePath}
          </span>
        );
      }
    }
  };

  // 🔧 BADGE DE STATUT AMÉLIORÉ avec indicateurs temps réel
  const getStatusBadge = (scheduledImport) => {
    const isRunning = runningImports.has(scheduledImport._id);

    // Vérifier si on est dans la plage horaire
    let isInTimeWindow = true;
    if (
      scheduledImport.enableDailyTimeWindow &&
      scheduledImport.dailyStartTime &&
      scheduledImport.dailyEndTime
    ) {
      isInTimeWindow = isCurrentlyInTimeWindow(
        scheduledImport.dailyStartTime,
        scheduledImport.dailyEndTime
      );
    }

    return (
      <div className="flex flex-col space-y-1">
        {/* Statut principal */}
        <span
          className={`px-2 py-1 text-xs rounded text-center font-medium ${
            scheduledImport.isActive
              ? "bg-green-100 text-green-800 border border-green-200"
              : "bg-red-100 text-red-800 border border-red-200"
          }`}
        >
          {scheduledImport.isActive ? "🟢 Active" : "🔴 Inactive"}
        </span>

        {/* Plage horaire */}
        {scheduledImport.enableDailyTimeWindow && (
          <span
            className={`px-2 py-1 text-xs rounded text-center font-medium ${
              isInTimeWindow
                ? "bg-purple-100 text-purple-800 border border-purple-200"
                : "bg-gray-100 text-gray-600 border border-gray-200"
            }`}
          >
            {isInTimeWindow ? "🕒 Dans plage" : "⏸️ Hors plage"}
          </span>
        )}

        {/* Statut d'exécution */}
        {(scheduledImport.lastStatus || isRunning) && (
          <span
            className={`px-2 py-1 text-xs rounded flex items-center justify-center font-medium ${
              isRunning || scheduledImport.lastStatus === "running"
                ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                : scheduledImport.lastStatus === "success"
                ? "bg-green-100 text-green-800 border border-green-200"
                : scheduledImport.lastStatus === "failed"
                ? "bg-red-100 text-red-800 border border-red-200"
                : "bg-gray-100 text-gray-800 border border-gray-200"
            }`}
          >
            {isRunning || scheduledImport.lastStatus === "running" ? (
              <>
                <FaSpinner className="animate-spin mr-1 w-3 h-3" />
                <span className="whitespace-nowrap">En cours...</span>
              </>
            ) : scheduledImport.lastStatus === "success" ? (
              <>
                <FaCheckCircle className="mr-1 w-3 h-3" />
                <span className="whitespace-nowrap">Succès</span>
              </>
            ) : scheduledImport.lastStatus === "failed" ? (
              <>
                <FaTimesCircle className="mr-1 w-3 h-3" />
                <span className="whitespace-nowrap">Échec</span>
              </>
            ) : (
              <>
                <FaClock className="mr-1 w-3 h-3" />
                <span className="whitespace-nowrap">En attente</span>
              </>
            )}
          </span>
        )}

        {/* 🔧 NOUVEAU: Affichage de l'erreur si présente */}
        {scheduledImport.lastError && (
          <span className="px-2 py-1 text-xs rounded flex items-center justify-center font-medium bg-red-50 text-red-700 border border-red-300">
            <FaExclamationTriangle className="mr-1 w-3 h-3" />
            <span className="whitespace-nowrap truncate max-w-xs" title={scheduledImport.lastError}>
              Erreur
            </span>
          </span>
        )}
      </div>
    );
  };

  const getRunButton = (scheduledImport) => {
    const isRunning = runningImports.has(scheduledImport._id);

    return (
      <button
        onClick={() => handleRun(scheduledImport)}
        disabled={isRunning}
        className={`p-2 rounded-md transition-colors ${
          isRunning
            ? "text-yellow-600 bg-yellow-50 cursor-not-allowed"
            : "text-green-600 hover:text-green-900 hover:bg-green-50"
        }`}
        title={isRunning ? "Importation en cours..." : "Exécuter maintenant"}
      >
        {isRunning ? (
          <FaSpinner className="animate-spin w-4 h-4" />
        ) : (
          <FaPlay className="w-4 h-4" />
        )}
      </button>
    );
  };

  if (loading) {
    return (
      <LoadingSpinner message="Chargement des importations planifiées..." />
    );
  }

  return (
    <div className="container mx-auto px-4">
      {/* En-tête avec rafraîchissement automatique */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-bold">📋 Importations Planifiées</h1>
          {refreshing && (
            <FaSync
              className="animate-spin text-blue-500"
              title="Actualisation automatique..."
            />
          )}
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            🔄 Mise à jour auto: 30s
          </span>
        </div>
        <button
          onClick={handleCreate}
          className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
        >
          ➕ Ajouter une importation planifiée
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <FaTimesCircle className="mr-2" />
          {error}
        </div>
      )}

      {/* Version desktop */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full bg-white shadow rounded-lg">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-1/4">
                📋 Type & Description
              </th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-1/5">
                📁 Source
              </th>
{/* 
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-1/6">
                ⏱️ Planning
              </th>
*/}
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-1/6">
                🕕 Plage Horaire
              </th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-1/6">
                📊 Statut
              </th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-1/5">
                📅 Prochaine exécution
              </th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-32">
                ⚙️ Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {scheduledImports.map((scheduledImport) => (
              <tr
                key={scheduledImport._id}
                className="hover:bg-gray-50 transition-colors"
              >
                <td className="px-6 py-4 align-top">
                  <div className="flex items-center space-x-2">
                    {getTypeIcon(scheduledImport.importType)}
                    {getDataTypeIcon(scheduledImport.dataType || "contracts")}
                    <div>
                      <div className="font-medium text-sm">
                        {scheduledImport.description}
                      </div>
                      <div className="text-xs text-gray-500">
                        {getTypeLabel(scheduledImport.importType)} -{" "}
                        {getDataTypeLabel(
                          scheduledImport.dataType || "contracts"
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 align-top">
                  <div className="max-w-xs">
                    {getSourceInfo(scheduledImport)}
                  </div>
                </td>

{/*}                
                <td className="px-6 py-4 align-top">
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs whitespace-nowrap">
                    {scheduledImport.cronSchedule}
                  </code>
                </td>
*/}


                <td className="px-6 py-4 align-top">
                  {scheduledImport.enableDailyTimeWindow ? (
                    <div className="space-y-1">
                      <div className="flex items-center space-x-1">
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded inline-flex items-center">
                          <FaClock className="w-3 h-3 mr-1" />
                          {scheduledImport.dailyStartTime} -{" "}
                          {scheduledImport.dailyEndTime}
                        </span>
                      </div>
                      {/* 🔧 SUPPRIMÉ: Plus d'affichage des liaisons automatiques */}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                      🌍 24h/24
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 align-top">
                  <div className="min-w-0">
                    {getStatusBadge(scheduledImport)}
                  </div>
                </td>
                <td className="px-6 py-4 align-top text-sm text-gray-500">
                  <div className="space-y-1">
                    <div className="whitespace-nowrap">
                      {scheduledImport.nextRun
                        ? new Date(scheduledImport.nextRun).toLocaleString(
                            "fr-FR",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )
                        : "❌ Non programmé"}
                    </div>
                    <div className="text-xs text-blue-600 font-medium">
                      {getTimeUntilNext(scheduledImport.nextRun)}
                    </div>
                    {/* 🔧 NOUVEAU: Affichage de la dernière exécution */}
                    {scheduledImport.lastRun && (
                      <div className="text-xs text-gray-400">
                        Dernière: {new Date(scheduledImport.lastRun).toLocaleString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 align-top text-right text-sm font-medium">
                  <div className="flex space-x-1 justify-end">
                    <button
                      onClick={() => handleEdit(scheduledImport)}
                      className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-md transition-colors"
                      title="Modifier"
                    >
                      <FaEdit className="w-4 h-4" />
                    </button>

                    {getRunButton(scheduledImport)}

                    <button
                      onClick={() => handleToggleStatus(scheduledImport)}
                      className={`p-2 rounded-md transition-colors ${
                        scheduledImport.isActive
                          ? "text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50"
                          : "text-green-600 hover:text-green-900 hover:bg-green-50"
                      }`}
                      title={
                        scheduledImport.isActive ? "Désactiver" : "Activer"
                      }
                    >
                      {scheduledImport.isActive ? (
                        <FaToggleOff className="w-4 h-4" />
                      ) : (
                        <FaToggleOn className="w-4 h-4" />
                      )}
                    </button>

                    <button
                      onClick={() => setConfirmDelete(scheduledImport)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                      title="Supprimer"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Version mobile/tablette */}
      <div className="lg:hidden space-y-4">
        {scheduledImports.map((scheduledImport) => (
          <div
            key={scheduledImport._id}
            className="bg-white shadow rounded-lg p-4 border-l-4 border-primary-500"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                {getTypeIcon(scheduledImport.importType)}
                {getDataTypeIcon(scheduledImport.dataType || "contracts")}
                <div>
                  <h3 className="font-medium">{scheduledImport.description}</h3>
                  <p className="text-xs text-gray-500">
                    {getTypeLabel(scheduledImport.importType)} -{" "}
                    {getDataTypeLabel(scheduledImport.dataType || "contracts")}
                  </p>
                </div>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => handleEdit(scheduledImport)}
                  className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-md"
                  title="Modifier"
                >
                  <FaEdit className="w-4 h-4" />
                </button>
                {getRunButton(scheduledImport)}
                <button
                  onClick={() => setConfirmDelete(scheduledImport)}
                  className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md"
                  title="Supprimer"
                >
                  <FaTrash className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <span className="font-medium text-gray-600">📁 Source:</span>
                <div className="mt-1 break-all">
                  {getSourceInfo(scheduledImport)}
                </div>
              </div>

              <div>
                <span className="font-medium text-gray-600">⏱️ Planning:</span>
                <code className="ml-2 bg-gray-100 px-2 py-1 rounded text-xs break-all">
                  {scheduledImport.cronSchedule}
                </code>
              </div>

              {scheduledImport.enableDailyTimeWindow && (
                <div>
                  <span className="font-medium text-gray-600">
                    🕕 Plage horaire:
                  </span>
                  <div className="mt-1">
                    <TimeWindowBadge scheduledImport={scheduledImport} />
                  </div>
                </div>
              )}

              <div>
                <span className="font-medium text-gray-600">📊 Statut:</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {getStatusBadge(scheduledImport)}
                </div>
              </div>

              {scheduledImport.nextRun && (
                <div>
                  <span className="font-medium text-gray-600">
                    📅 Prochaine exécution:
                  </span>
                  <div className="mt-1 space-y-1">
                    <span className="ml-2 text-gray-500 break-all">
                      {new Date(scheduledImport.nextRun).toLocaleString()}
                    </span>
                    <div className="text-xs text-blue-600 font-medium ml-2">
                      {getTimeUntilNext(scheduledImport.nextRun)}
                    </div>
                  </div>
                </div>
              )}

              {/* 🔧 NOUVEAU: Affichage de l'erreur en mobile */}
              {scheduledImport.lastError && (
                <div>
                  <span className="font-medium text-gray-600">❌ Dernière erreur:</span>
                  <div className="mt-1 text-xs text-red-600 bg-red-50 p-2 rounded border">
                    {scheduledImport.lastError}
                  </div>
                </div>
              )}

              <div className="pt-2 border-t">
                <button
                  onClick={() => handleToggleStatus(scheduledImport)}
                  className={`text-sm font-medium flex items-center ${
                    scheduledImport.isActive
                      ? "text-yellow-600 hover:text-yellow-900"
                      : "text-green-600 hover:text-green-900"
                  }`}
                >
                  {scheduledImport.isActive ? (
                    <>
                      <FaToggleOff className="inline mr-1" /> 🔴 Désactiver
                    </>
                  ) : (
                    <>
                      <FaToggleOn className="inline mr-1" /> 🟢 Activer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {scheduledImports.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="text-gray-500 mb-4">
            <FaCloud className="mx-auto h-12 w-12 mb-2" />
            <p className="text-lg font-medium">
              Aucune importation planifiée configurée
            </p>
            <p className="text-sm">
              Créez votre première importation automatisée pour commencer
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
          >
            ➕ Créer la première importation
          </button>
        </div>
      )}

      {/* Modal de création/modification */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                {currentImport
                  ? "✏️ Modifier l'importation planifiée"
                  : "➕ Ajouter une importation planifiée"}
              </h3>
              <ScheduledImportForm
                scheduledImport={currentImport}
                onSubmit={handleFormSubmit}
                onCancel={() => setShowModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center text-red-600">
              🗑️ Confirmer la suppression
            </h3>
            <p className="mb-4">
              Êtes-vous sûr de vouloir supprimer l'importation planifiée :
              <br />
              <strong className="text-red-600">
                "{confirmDelete.description}"
              </strong>{" "}
              ?
            </p>
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
              >
                ❌ Annuler
              </button>
              <button
                onClick={handleDelete}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                🗑️ Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 🔧 COMPOSANT: Badge pour afficher la plage horaire (SIMPLIFIÉ)
const TimeWindowBadge = ({ scheduledImport }) => {
  if (!scheduledImport.enableDailyTimeWindow) return null;

  return (
    <div className="flex items-center space-x-1 mt-1">
      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded flex items-center">
        <FaClock className="w-3 h-3 mr-1" />
        {scheduledImport.dailyStartTime} - {scheduledImport.dailyEndTime}
      </span>
      {/* 🔧 SUPPRIMÉ: Plus de badges pour les liaisons automatiques */}
    </div>
  );
};

export default ScheduledImportsList;